import {
  type MultipartInitiateRequest,
  type MultipartInitiateResponse,
  type MultipartCompleteRequest,
  type MultipartCompleteResponse,
  multipartInitiateResponseSchema,
} from "@mg/schemas/src/biggie/upload";

import { UPLOAD_API_URL } from "../config/env";
import { posthogClient } from "../config/posthog";
import { smartFetch } from "../utils/http";
import {
  UploadCancelledError,
  uploadWithStallDetection,
} from "../utils/stalledUpload";

const getUserContext = () => {
  const personProps =
    posthogClient.get_property("$stored_person_properties") || {};
  return {
    user_id: posthogClient.get_distinct_id(),
    user_email: personProps.email,
    user_name: personProps.name,
  };
};

// Types
export interface MultipartUploadInitiateResponse {
  uploadId: string;
  key: string;
  bucket: string;
  parts: {
    partNumber: number;
    uploadUrl: string;
  }[];
}

export interface UploadProgress {
  totalParts: number;
  completedParts: number;
  totalBytes: number;
  uploadedBytes: number;
}

export interface MultipartUploadOptions {
  useAcceleration?: boolean;
  onProgress?: (progress: {
    totalParts: number;
    completedParts: number;
    totalBytes: number;
    uploadedBytes: number;
  }) => void;
  abortSignal?: AbortSignal;
}

// Constants
export const DEFAULT_BLOCK_SIZE = 5 * 1024 * 1024; // 5MB
// why: S3 answers HTTP/1.1, so the browser already caps a host at ~6
// connections; starting every part at once only queues requests in the browser.
export const MULTIPART_UPLOAD_CONCURRENCY = 6;
// Bound on failedPartNumbers so a failing network cannot inflate the payload.
export const FAILED_PART_NUMBERS_CAP = 20;
const MAX_PART_ATTEMPTS = 3;
const UPLOAD_CANCELLED_MESSAGE = "Upload was cancelled";

/**
 * Per-upload counters that uploadPart fills in. Telemetry is sent once per
 * upload from these, never per part: a 500 MB file has 100 parts, and one
 * capture per part drained PostHog's client burst budget (#7002).
 */
export interface PartUploadStats {
  completedParts: number;
  abortedParts: number;
  retriedParts: number;
  failedParts: number;
  failedPartNumbers: number[];
  partDurationsMs: number[];
}

export function createPartUploadStats(): PartUploadStats {
  return {
    completedParts: 0,
    abortedParts: 0,
    retriedParts: 0,
    failedParts: 0,
    failedPartNumbers: [],
    partDurationsMs: [],
  };
}

export function summarizePartDurations(durations: number[]): {
  slowestPartMs: number | null;
  medianPartMs: number | null;
} {
  if (durations.length === 0) {
    return { slowestPartMs: null, medianPartMs: null };
  }
  const sorted = [...durations].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const medianPartMs =
    sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  return { slowestPartMs: sorted[sorted.length - 1], medianPartMs };
}

/**
 * Runs `run` over `items` with at most `concurrency` in flight. Rejects with
 * the first error as soon as it happens; after that, or once `abortSignal`
 * fires, no new item is started. Results are in the order items settled, not
 * the order of `items`.
 */
function runWithPool<T, R>(
  items: T[],
  concurrency: number,
  run: (item: T) => Promise<R>,
  abortSignal?: AbortSignal,
): Promise<R[]> {
  return new Promise((resolve, reject) => {
    const results: R[] = [];
    let nextIndex = 0;
    let stopped = false;

    const fail = (error: unknown) => {
      stopped = true;
      reject(error);
    };

    const startNext = () => {
      if (stopped || nextIndex >= items.length) return;
      if (abortSignal?.aborted) {
        fail(new Error(UPLOAD_CANCELLED_MESSAGE));
        return;
      }
      run(items[nextIndex++]).then((result) => {
        results.push(result);
        if (results.length === items.length) {
          resolve(results);
        } else {
          startNext();
        }
      }, fail);
    };

    if (items.length === 0) {
      resolve(results);
      return;
    }
    for (let i = 0; i < Math.min(concurrency, items.length); i++) {
      startNext();
    }
  });
}

// Utility functions
export function supportsMultipartUpload(): boolean {
  return typeof window !== "undefined" && "Blob" in window;
}

export function createProgressTracker(
  totalParts: number,
  totalBytes: number,
): UploadProgress {
  return {
    totalParts,
    completedParts: 0,
    totalBytes,
    uploadedBytes: 0,
  };
}

// Core multipart upload functions
export async function initiateMultipartUpload(
  filename: string,
  fileSize: number,
  fileType: string,
  useAcceleration = false,
  blockSize = DEFAULT_BLOCK_SIZE,
  isRevisionFile = false,
): Promise<MultipartInitiateResponse> {
  const now = performance.now();

  posthogClient.capture("multipart_upload_initiate_start", {
    ...getUserContext(),
    filename,
    fileSize,
    fileType,
    useAcceleration,
    blockSize,
    initiatedAt: now,
    isRevisionFile,
  });

  try {
    const json = await smartFetch(`${UPLOAD_API_URL}/multipart/initiate`, {
      method: "POST",
      body: JSON.stringify({
        filename,
        fileSize,
        fileType,
        useAcceleration,
        blockSize,
        isRevisionFile,
      }),
    });

    const parsedData = multipartInitiateResponseSchema.parse(json);
    const newNow = performance.now();

    posthogClient.capture("multipart_upload_initiate_complete", {
      ...getUserContext(),
      elapsed: newNow - now,
    });

    return parsedData;
  } catch (e) {
    const newNow = performance.now();
    posthogClient.capture("multipart_upload_initiate_error", {
      ...getUserContext(),
      error: (e as Error).message,
      elapsed: newNow - now,
    });
    throw e;
  }
}

export async function uploadPart(
  partUrl: string,
  partNumber: number,
  chunk: Blob,
  onProgress?: (uploadedBytes: number) => void,
  abortSignal?: AbortSignal,
  stats: PartUploadStats = createPartUploadStats(),
): Promise<{ ETag: string }> {
  const now = performance.now();
  const maxRetries = MAX_PART_ATTEMPTS;
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      const response = await uploadWithStallDetection({
        method: "PUT",
        url: partUrl,
        body: chunk,
        abortSignal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to upload part ${partNumber}: ${response.status} ${response.statusText}`,
        );
      }

      const etag = response.getHeader("ETag");
      if (!etag) {
        throw new Error(`No ETag received for part ${partNumber}`);
      }

      stats.completedParts++;
      stats.partDurationsMs.push(performance.now() - now);

      if (onProgress) {
        onProgress(chunk.size);
      }

      return { ETag: etag };
    } catch (e) {
      if (e instanceof UploadCancelledError) {
        stats.abortedParts++;
        throw new Error(UPLOAD_CANCELLED_MESSAGE);
      }
      retryCount++;
      if (retryCount === 1) {
        stats.retriedParts++;
      }
      if (retryCount === maxRetries) {
        stats.failedParts++;
        if (stats.failedPartNumbers.length < FAILED_PART_NUMBERS_CAP) {
          stats.failedPartNumbers.push(partNumber);
        }
        throw e;
      }
      // Wait before retrying (exponential backoff)
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, retryCount) * 1000),
      );
    }
  }

  throw new Error(
    `Failed to upload part ${partNumber} after ${maxRetries} retries`,
  );
}

export async function completeMultipartUpload(
  uploadId: string,
  key: string,
  parts: { partNumber: number; etag: string }[],
): Promise<{
  location: string;
  bucket: string;
  key: string;
  etag: string;
}> {
  const now = performance.now();

  posthogClient.capture("multipart_upload_complete_start", {
    ...getUserContext(),
    key,
    uploadId,
    partsCount: parts.length,
    initiatedAt: now,
  });

  try {
    const response = await smartFetch(`${UPLOAD_API_URL}/multipart/complete`, {
      method: "POST",
      body: JSON.stringify({
        uploadId,
        key,
        parts,
      }),
    });

    const parsedResponse = response as {
      location: string;
      bucket: string;
      key: string;
      etag: string;
    };
    const newNow = performance.now();

    posthogClient.capture("multipart_upload_complete_finish", {
      ...getUserContext(),
      elapsed: newNow - now,
    });

    return parsedResponse;
  } catch (e) {
    const newNow = performance.now();
    posthogClient.capture("multipart_upload_complete_error", {
      ...getUserContext(),
      error: (e as Error).message,
      elapsed: newNow - now,
    });
    throw e;
  }
}

// Main multipart upload function
export async function uploadFileWithMultipart(
  file: File,
  options: MultipartUploadOptions = {},
  isRevisionFile = false,
): Promise<{ key: string; location: string }> {
  if (!supportsMultipartUpload()) {
    throw new Error("Multipart upload is not supported in this environment");
  }

  const { useAcceleration = false, onProgress, abortSignal } = options;
  const fileSize = file.size;
  const blockSize = DEFAULT_BLOCK_SIZE;
  const totalParts = Math.ceil(fileSize / blockSize);

  // Step 1: Initiate multipart upload
  const initiateRequest: MultipartInitiateRequest = {
    body: {
      filename: file.name,
      fileSize,
      fileType: file.type,
      useAcceleration,
      blockSize,
      isRevisionFile,
    },
  };

  const initiateResponse = (await smartFetch(
    `${UPLOAD_API_URL}/multipart/initiate`,
    {
      method: "POST",
      body: JSON.stringify(initiateRequest.body),
    },
  )) as MultipartInitiateResponse;

  const { uploadId, key, parts } = initiateResponse;

  // Step 2: Upload each part, at most MULTIPART_UPLOAD_CONCURRENCY at once
  const uploadedParts: { partNumber: number; etag: string }[] = [];
  let uploadedBytes = 0;
  const stats = createPartUploadStats();
  let status: "success" | "error" | "aborted" = "error";
  let phase: "parts" | "complete" = "parts";
  let completeResponse: MultipartCompleteResponse;

  posthogClient.capture("multipart_upload_start", {
    ...getUserContext(),
    partsCount: parts.length,
    fileSize,
    blockSize,
  });
  const startedAt = performance.now();

  try {
    const results = await runWithPool(
      parts,
      MULTIPART_UPLOAD_CONCURRENCY,
      async ({ partNumber, uploadUrl }) => {
        const start = (partNumber - 1) * blockSize;
        const end = Math.min(start + blockSize, fileSize);
        const chunk = file.slice(start, end);

        const { ETag: etag } = await uploadPart(
          uploadUrl,
          partNumber,
          chunk,
          (bytes) => {
            uploadedBytes += bytes;
            onProgress?.({
              totalParts,
              // why: uploadPart bumps stats.completedParts before it reports
              // progress; uploadedParts stays empty until the pool resolves.
              completedParts: stats.completedParts,
              totalBytes: fileSize,
              uploadedBytes,
            });
          },
          abortSignal,
          stats,
        );
        return { partNumber, etag };
      },
      abortSignal,
    );
    // The pool returns parts in completion order; S3 needs them by part number
    uploadedParts.push(...results.sort((a, b) => a.partNumber - b.partNumber));

    // Step 3: Complete multipart upload. It stays inside this try so the
    // finish event's status covers the whole upload, not just the parts.
    phase = "complete";
    const completeRequest: MultipartCompleteRequest = {
      body: {
        uploadId,
        key,
        parts: uploadedParts,
      },
    };

    completeResponse = (await smartFetch(
      `${UPLOAD_API_URL}/multipart/complete`,
      {
        method: "POST",
        body: JSON.stringify(completeRequest.body),
      },
    )) as MultipartCompleteResponse;
    status = "success";
  } catch (e) {
    const message = (e as Error).message;
    status = message === UPLOAD_CANCELLED_MESSAGE ? "aborted" : "error";
    // Runs once per upload: later failures only show up in the finish counts.
    if (status === "error") {
      posthogClient.capture("multipart_upload_error", {
        ...getUserContext(),
        phase,
        // The pool rejects on the first part to exhaust its retries, so this
        // is that part. There is no part number when the complete call failed.
        partNumber: phase === "parts" ? stats.failedPartNumbers[0] : undefined,
        error: message,
        attempts: phase === "parts" ? MAX_PART_ATTEMPTS : undefined,
      });
    }
    throw e;
  } finally {
    posthogClient.capture("multipart_upload_finish", {
      ...getUserContext(),
      status,
      partsCount: parts.length,
      elapsed: performance.now() - startedAt,
      completedParts: stats.completedParts,
      abortedParts: stats.abortedParts,
      retriedParts: stats.retriedParts,
      failedParts: stats.failedParts,
      failedPartNumbers: [...stats.failedPartNumbers],
      ...summarizePartDurations(stats.partDurationsMs),
    });
  }

  return {
    key: completeResponse.key,
    location: completeResponse.location,
  };
}
